import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TeamBaseService } from 'src/app/_services/dispo/team-base';

export interface DispatchCalendarAppointment {
  id: number;
  teamOrderId: number;
  team?: number;
  start: string;
  end: string;
  timeLabel: string;
  title: string;
  address: string;
  orderNumber: string;
  typeName: string;
  typeColor: string;
  reserved: boolean;
  isCanceled: boolean;
  lat: number | null;
  lng: number | null;
}

export interface DispatchTerminSource {
  termin?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DispatchPlannerCalendarService {
  constructor(private teamBaseService: TeamBaseService) {}

  resolvePlanningDate(stops: DispatchTerminSource[]): string | null {
    const counts = new Map<string, number>();
    for (const stop of stops) {
      const parsed = this.parseTerminDate(stop.termin);
      if (parsed) {
        counts.set(parsed, (counts.get(parsed) || 0) + 1);
      }
    }
    if (!counts.size) {
      return null;
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
  }

  parseTerminDate(termin?: string): string | null {
    const raw = (termin || '').trim();
    if (!raw) {
      return null;
    }

    const deMatch = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(raw);
    if (deMatch) {
      return `${deMatch[3]}-${deMatch[2].padStart(2, '0')}-${deMatch[1].padStart(2, '0')}`;
    }

    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }

    return null;
  }

  loadTeamAppointments(
    teamBaseId: number,
    planningDate: string,
    branchId: number | null
  ): Observable<DispatchCalendarAppointment[]> {
    const dayStart = new Date(`${planningDate}T00:00:00`);
    const dayEnd = new Date(`${planningDate}T23:59:59.999`);
    if (Number.isNaN(dayStart.getTime())) {
      return of([]);
    }

    const useCalendarMode = branchId != null;
    const payload: Record<string, unknown> = {
      start: dayStart.toISOString(),
      end: dayEnd.toISOString(),
      mode: useCalendarMode ? 'calendar' : 'scheduler',
      includeMasterEvents: false,
      includeTeamsList: false
    };
    if (useCalendarMode) {
      payload.order = { customer_branch_id: branchId };
    }

    const resourceId = `O${teamBaseId}`;

    return this.teamBaseService.calendarAllByDateRange(payload, payload.mode as string).pipe(
      map((response: any) => {
        const events = Array.isArray(response?.events) ? response.events : [];
        return events
          .filter((event: any) => {
            if ((event?.resource || '') !== resourceId) {
              return false;
            }
            if (!event?.start) {
              return false;
            }
            return this.isSameDay(event.start, planningDate);
          })
          .map((event: any) => this.mapCalendarAppointment(event))
          .sort((a, b) => a.start.localeCompare(b.start));
      }),
      catchError(() => of([]))
    );
  }

  private mapCalendarAppointment(event: any): DispatchCalendarAppointment {
    const order = event?.order || {};
    const person = order?.root_order?.person || order?.rootOrder?.person || {};
    const firstName = person?.first_name || order?.first_name || '';
    const lastName = person?.last_name || order?.last_name || '';
    const title = `${firstName} ${lastName}`.trim() || order?.order_number || 'Kalender-Termin';
    const teamOrderType = event?.team_order_type || event?.teamOrderType || {};
    const street = person?.street || order?.street || '';
    const postal = person?.postal_code || order?.postal_code || '';
    const place = person?.place || order?.place || '';
    const address = [street, [postal, place].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const lat = this.resolveCoordinate(
      person?.lat,
      order?.lat,
      order?.root_order?.lat,
      order?.rootOrder?.lat
    );
    const lng = this.resolveCoordinate(
      person?.lng,
      order?.lng,
      order?.root_order?.lng,
      order?.rootOrder?.lng
    );

    return {
      id: Number(event.id),
      teamOrderId: Number(event.id),
      start: event.start,
      end: event.end,
      timeLabel: `${this.formatTime(event.start)}–${this.formatTime(event.end)}`,
      title,
      address,
      orderNumber: order?.order_number || '',
      typeName: teamOrderType?.name || teamOrderType?.label || 'Termin',
      typeColor: teamOrderType?.color || '#6c757d',
      reserved: !!event?.reserved,
      isCanceled: !!event?.is_canceled,
      lat,
      lng
    };
  }

  private resolveCoordinate(...values: unknown[]): number | null {
    for (const value of values) {
      if (value == null || value === '') {
        continue;
      }
      const num = Number(value);
      if (Number.isFinite(num)) {
        return num;
      }
    }
    return null;
  }

  private formatTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  private isSameDay(value: string, planningDate: string): boolean {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    const local = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return local === planningDate;
  }
}
